import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { IExternalUrl, IReport, IReportList, ReportService } from '@services/report.service';
import { IUserAccess, UserService } from '@services/user.service';
import { ExternalUrlService } from '@shared/services/externalurl.service';
import { CountdownComponent, CountdownConfig, CountdownEvent } from 'ngx-countdown';
import { models } from 'powerbi-client';
import { PowerBIReportEmbedComponent } from 'powerbi-client-angular';
import { combineLatest, filter, skipWhile, Subject, take, takeUntil } from 'rxjs';
import { EmbedconfigService, IEmbedConfig } from '../../shared/services/embedconfig.service';
import { InsightsService } from '../../shared/services/insights.service';
import { SessionStorageService } from '../../shared/services/storage.service';

@Component({
	selector: 'app-slideshow',
	templateUrl: './slideshow.component.html',
	styleUrls: ['./slideshow.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class SlideshowComponent implements OnInit, OnDestroy {
	@ViewChild(PowerBIReportEmbedComponent)
	reportObj!: PowerBIReportEmbedComponent;

	@ViewChild('cd', { static: true })
	countdown!: CountdownComponent;

	reportConfig: IEmbedConfig = {
		id: null,
		groupId: null,
		type: 'report',
		embedUrl: undefined,
		tokenType: models.TokenType.Embed,
		accessToken: undefined,
		settings: undefined,
		expiration: undefined,
	};

	countdownConfig: CountdownConfig = {
		leftTime: null,
		format: 'mm:ss',
	};

	reportDivClassName: string = 'powerbi-embed-class';
	fullscreen: boolean = true;
	unsubscribe: Subject<void> = new Subject<void>();
	keepAliveTimeout: any = false;
	private networkTimeout = 120; // seconds
	retryInterval: number;
	errorTimeout: number;
	isLoaded: boolean;

	private skipToNextErrors: string[] = ['ExplorationContainer_FailedToLoadModel_DefaultDetails'];

	reportEventHandlers = new Map([
		[
			'loaded',
			() => {
				this.isLoaded = true;
				clearTimeout(this.errorTimeout);
			},
		],
		[
			'rendered',
			() => {
				this.countdown.restart();
				this.countdown.stop();
				if (this.isLoaded) {
					this.countdown.restart();
				}
				clearTimeout(this.keepAliveTimeout);
			},
		],
		[
			'error',
			(event) => {
				if (!this.skipToNextErrors.includes(event?.detail?.message)) {
					// If the error isn't serious continue as usual
					return;
				}
				// If the error is serious (i.e. missing data model), only show the report for 10 seconds and then move on to next report
				this.errorTimeout = window.setTimeout(() => {
					console.log('Slideshow error - skipping to next report', event);
					this.HandleCountdownFinished();
				}, 10 * 1000);
			},
		],
	]);

	onTimerFinished(e: CountdownEvent) {
		if (e.action === 'done') {
			this.HandleCountdownFinished();
		}
	}

	resetTimeout() {
		if (this.keepAliveTimeout) {
			clearTimeout(this.keepAliveTimeout);
		}
		this.keepAliveTimeout = setTimeout(() => {
			console.log('Reloading after 120 seconds due to suspected network error');
			this.unsubscribe.next();
			this.unsubscribe.complete();
			this.setup();
		}, this.networkTimeout * 1000);
	}

	constructor(
		private embedService: EmbedconfigService,
		private insightsService: InsightsService,
		public router: Router,
		private sessionStorageService: SessionStorageService,
		private reportService: ReportService,
		private externalUrlService: ExternalUrlService,
		private userService: UserService,
	) {}

	ngOnDestroy() {
		clearTimeout(this.keepAliveTimeout);
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	setup() {
		this.resetTimeout();
		this.embedService.reportsLoaded$
			.pipe(
				take(1),
				filter((v) => v === true),
			)
			.subscribe(() => {
				console.log('reports loaded subject called');
				this.embedService.embedConfig$
					.pipe(
						takeUntil(this.unsubscribe),
						skipWhile((embed) => !this.reportService.userReports.map((r) => r.externalId).includes(embed?.id)),
					)
					.subscribe((embed) => {
						this.countdown.stop();
						if (embed) {
							this.insightsService
								.fetchUserById()
								.pipe(takeUntil(this.unsubscribe))
								.subscribe((v) => {
									console.log('embedconfig updated');
									this.reportConfig = { ...embed };
									this.reportConfig.viewMode = 0;
									this.reportConfig.settings = {
										filterPaneEnabled: false,
										navContentPaneEnabled: false,
									};

									const thisReport = this.reportObj?.getReport();
									if (thisReport) {
										console.log('setting new accesstoken');
										void thisReport.setAccessToken(embed.accessToken);
									}

									const frame = this.getEmbedFrame();
									if (frame) {
										console.log('clearing powerbi iframe cache');
										frame.contentWindow.location.href = embed.embedUrl;
									}

									this.countdown.config.leftTime = 60;
									if (v.result?.slideshowInterval && v.result.slideshowInterval > 0) {
										this.countdown.config.leftTime = v.result.slideshowInterval;
									}

									// Use slideshow interval from impersonated user if it exists
									if (this.sessionStorageService.get('App.ImpersonateUser')) {
										const tmp: IUserAccess = JSON.parse(this.sessionStorageService.get('App.ImpersonateUser'));
										if (tmp.interval && tmp.interval > 0) {
											this.countdown.config.leftTime = tmp.interval;
										}
									}

									if (!embed.groupId) {
										// Start timer for external urls
										setTimeout(() => this.countdown.restart(), 2000);
									}
								});
						}
					});
			});
	}

	ngOnInit(): void {
		this.setup();
	}

	private HandleCountdownFinished(): void {
		this.isLoaded = false;
		this.countdown.stop();
		let configs: IEmbedConfig[] = this.embedService.embedConfigs;

		combineLatest([
			this.reportService.fetchUserReportsById(this.userService.impersonateUser ? this.userService.impersonateUser.id : this.sessionStorageService.get('User.IAM.Id')),
			this.externalUrlService.fetchUserExternalUrlsById(this.userService.impersonateUser ? this.userService.impersonateUser.id : this.sessionStorageService.get('User.IAM.Id')),
		]).subscribe((data) => {
			if (data[0] && data[1]) {
				console.log('assigned reports ', data[0].result.length, ', assigned extUrls ', data[1].result.length, ', embedconfigs ', configs.length);
				const userReports: IReportList = data[0].result;
				const userExtUrls: IExternalUrl[] = data[1].result;
				const slideshowReportIds = userReports.map((r) => r.externalId);
				const extUrlIds = userExtUrls.map((r) => r.url);
				const slideshowIds = [...slideshowReportIds, ...extUrlIds];
				if (configs) {
					configs = configs.filter((c) => slideshowIds.includes(c.id));
					console.log('slideshow reports and urls with embedconfigs ', configs.length);
					slideshowIds.forEach((id) => {
						if (configs.filter((c) => c.id === id).length === 0) {
							this.embedService.setLocalEmbedConfig('report', id, userReports.filter((r) => r.externalId === id).length > 0 ? userReports.filter((r) => r.externalId === id)[0].groupId : null, false);
						}
					});
					const index: number = configs.findIndex((x) => x.id === this.reportConfig.id);
					const next: number = index < configs.length - 1 ? index + 1 : 0;
					if (configs && configs[next]) {
						clearInterval(this.retryInterval);
						this.embedService.setLocalEmbedConfig(configs[next].type, configs[next].id, configs[next].groupId);
					} else {
						if (!this.retryInterval) {
							this.retryInterval = window.setInterval(() => this.HandleCountdownFinished(), 2000);
						}
					}
					console.log(`timestamp: ${new Date().toISOString()}`);
					console.log(`[${index}] -> [${next}] > [0..${configs.length - 1}]`);
				}
			}
		});
	}

	toggleFullscreen = () => {
		if (this.fullscreen) {
			const userReport = JSON.parse(this.sessionStorageService.get('User.Report')) as IReport;
			this.reportService.report = userReport;
			void this.router.navigate(['insights/reports']);
			this.sessionStorageService.set('Selected.ServiceName', 'AciesInsights');
		}
	};

	getEmbedFrame = () => this.reportObj?.getReport()?.element?.firstChild as HTMLIFrameElement;

	getDOMDocument = () => this.reportObj?.getReport()?.element?.getRootNode() as Document;
}
